import SEO from "../components/seo/SEO";
import Breadcrumbs from "../components/seo/Breadcrumbs";
import { DATA_SOURCES, DATA_YEAR } from "../lib/financial-data/canada";

function LegalShell({ title, path, description, children }) {
  return (
    <>
      <SEO title={`${title} — MyFinancesGuru Tools`} description={description} path={path} />
      <div className="mx-auto max-w-3xl px-4 py-12 sm:px-6 lg:px-8">
        <Breadcrumbs items={[{ label: "Home", to: "/" }, { label: title, to: path }]} />
        <h1 className="font-heading text-4xl font-bold tracking-tight text-foreground sm:text-5xl">{title}</h1>
        <p className="mt-3 text-sm text-muted-foreground">Last updated: {new Date().toLocaleDateString("en-CA")}</p>
        <div className="prose-legal mt-8 space-y-5 text-base leading-relaxed text-foreground/80">{children}</div>
      </div>
    </>
  );
}

const H = ({ children }) => <h2 className="font-heading text-xl font-bold text-foreground">{children}</h2>;

export function Privacy() {
  return (
    <LegalShell title="Privacy Policy" path="/privacy" description="How MyFinancesGuru Tools handles your data. We never store your financial calculations — only optional newsletter email addresses.">
      <p>Your privacy matters. This policy explains what we collect and, just as importantly, what we don't.</p>
      <H>Calculations stay on your device</H>
      <p>All financial calculations run locally in your browser. The numbers you enter into any calculator are <strong>never sent to or stored on our servers</strong>.</p>
      <H>What we collect</H>
      <ul className="list-disc space-y-1 pl-5">
        <li><strong>Newsletter / lead signups:</strong> if you choose to download a free resource, we store the email address (and optional first name) you provide.</li>
        <li><strong>Contact form:</strong> the name, email and message you submit so we can reply.</li>
        <li><strong>Anonymous analytics:</strong> we use privacy-conscious analytics (e.g. GA4) to understand which pages and tools are popular. We do <strong>not</strong> send your financial input values to any analytics service.</li>
      </ul>
      <H>How we use it</H>
      <p>To deliver requested resources, send occasional newsletters (you can unsubscribe anytime), respond to enquiries, and improve the site. We do not sell your personal information.</p>
      <H>Third parties</H>
      <p>Digital product checkout and delivery are handled by <strong>Gumroad</strong> under their own privacy policy. Email delivery may use a third-party email service provider.</p>
      <H>Your rights</H>
      <p>You may request access to or deletion of your data at any time via our <a href="/contact" className="text-brand hover:text-action">contact page</a>.</p>
    </LegalShell>
  );
}

export function Terms() {
  return (
    <LegalShell title="Terms of Use" path="/terms" description="The terms governing your use of MyFinancesGuru Tools' free calculators and content.">
      <p>By using MyFinancesGuru Tools you agree to these terms.</p>
      <H>Educational use only</H>
      <p>All calculators, results and content are provided for general educational and informational purposes only. They are not financial, tax, legal or investment advice.</p>
      <H>No warranty</H>
      <p>The tools are provided "as is" without warranties of any kind. While we strive for accuracy, we do not guarantee that results are correct, complete or current. You are responsible for verifying figures before making decisions.</p>
      <H>Limitation of liability</H>
      <p>MyFinancesGuru and its operators are not liable for any losses or damages arising from your use of, or reliance on, the tools or content.</p>
      <H>Products</H>
      <p>Digital products are sold and delivered through Gumroad, subject to Gumroad's terms and refund policies.</p>
      <H>Changes</H>
      <p>We may update these terms and the calculators at any time. Continued use constitutes acceptance of any changes.</p>
    </LegalShell>
  );
}

export function DisclaimerPage() {
  return (
    <LegalShell title="Disclaimer" path="/disclaimer" description="Important disclaimer for MyFinancesGuru Tools' Canadian financial calculators. Estimates only — not financial advice.">
      <div className="rounded-xl border border-amber-200 bg-amber-50 p-5 text-amber-900">
        <p><strong>The calculators and content on this site are for educational purposes only and do not constitute financial, tax, legal or investment advice.</strong></p>
      </div>
      <H>Estimates, not guarantees</H>
      <p>Results are simplified estimates based on the inputs you provide and general assumptions. Real-world outcomes depend on many factors we can't model, including your full tax situation, market performance, fees and changing rules.</p>
      <H>Canadian data</H>
      <p>Canadian figures (contribution limits, tax brackets, benefit amounts and mortgage rules) reflect the <strong>{DATA_YEAR}</strong> tax year and are provided as a convenience. They must be verified against official sources before you rely on them:</p>
      <ul className="list-disc space-y-1 pl-5">
        {DATA_SOURCES.map((d, i) => <li key={i}><strong>{d.label}:</strong> {d.source} ({d.year})</li>)}
      </ul>
      <H>Seek professional advice</H>
      <p>Always consult a qualified financial planner, accountant or licensed professional before making decisions about your money.</p>
    </LegalShell>
  );
}
